import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import PreDashboardBuild from '@/components/PreDashboardBuild';

const DESTINATION_KEY = 'pre_dashboard_destination';
const PROGRAM_FLOW_INCUBATOR_NAME_KEY = 'pre_dashboard_incubator_name';
const LAST_SEEN_PREFIX = 'founderboard_pre_dashboard_last_seen:';

const getLastSeenKey = (user) => {
  const identity = user?._id || user?.id || user?.email || user?.username || 'default';
  return `${LAST_SEEN_PREFIX}${identity}`;
};

const getDashboardRoute = (user) => {
  if (user?.role === 'vc') return '/vc/chat';
  return '/startup/dashboard';
};

const PreDashboard = ({ user }) => {
  const navigate = useNavigate();

  // Keep the handoff destination in sessionStorage for the full 10-second build.
  // That survives the deliberate full-page redirect from Login.jsx and also
  // survives an accidental refresh while this screen is running.
  const destination = useMemo(
    () => sessionStorage.getItem(DESTINATION_KEY) || getDashboardRoute(user),
    [user]
  );
  const incubatorName = useMemo(
    () => sessionStorage.getItem(PROGRAM_FLOW_INCUBATOR_NAME_KEY),
    []
  );
  const programHeadlineSchedule = useMemo(
    () => incubatorName ? [
      {
        text: (
          <>
            Sharing your form with
            <br />
            {incubatorName}...
          </>
        ),
        durationMs: 4000,
      },
      { text: 'Connecting you to other Incubators & Programs...', durationMs: 3000 },
      { text: 'Stitching together the final details...', durationMs: 3000 },
    ] : null,
    [incubatorName]
  );

  // Count this screen as "seen" as soon as it is actually displayed. This makes
  // the 7-day cooldown survive logout, browser restarts, and new login sessions.
  useEffect(() => {
    if (!user) return;
    localStorage.setItem(getLastSeenKey(user), String(Date.now()));
  }, [user]);

  const handleContinue = React.useCallback(() => {
    sessionStorage.removeItem(DESTINATION_KEY);
    sessionStorage.removeItem(PROGRAM_FLOW_INCUBATOR_NAME_KEY);
    navigate(destination, { replace: true });
  }, [destination, navigate]);

  return (
    <PreDashboardBuild
      continueLabel="Go to Dashboard"
      onContinue={handleContinue}
      headlineSchedule={programHeadlineSchedule}
      autoContinueDelayMs={incubatorName ? 3000 : null}
    />
  );
};

export default PreDashboard;
